import { AlertCircle, Check, X, type LucideIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import type { Translate } from "@/constants/i18n";
import { useTranslation } from "@/store/locale";
import { cn } from "@/utils/utils";

/**
 * One control shell, shared by every text input, textarea and select.
 *
 * A hairline at rest, the accent plus a soft ring on focus, and the same ring
 * in red when the value is rejected. Keeping it in one string is what stops
 * three different field heights appearing on three different screens.
 */
export const controlClass = [
  "w-full rounded-field border border-line-strong bg-surface-raised text-fg",
  "transition-[border-color,box-shadow,background-color] duration-[var(--duration-fast)]",
  "outline-none placeholder:text-fg-subtle",
  "focus-visible:outline-none focus-visible:border-accent focus-visible:ring-[3px] focus-visible:ring-[var(--accent-ring)]",
  // Red at rest and red in the cursor: `focus-visible:border-accent` above is
  // one variant deep and would otherwise win back the border the moment the
  // field being complained about is the field being typed in, leaving a blue
  // outline around a red message.
  "aria-[invalid=true]:border-danger aria-[invalid=true]:focus-visible:border-danger",
  "aria-[invalid=true]:focus-visible:ring-[var(--danger-soft)]",
  "disabled:cursor-not-allowed disabled:bg-surface-disabled disabled:text-fg-subtle",
  // Scoped to the fields that can actually be made read-only. `:read-only`
  // matches every element that simply is not editable — a <button>, a <select>,
  // the <span> a unit input is built from — so unscoped it sank the shell of
  // every picker in the app to the greyed-out surface and made them read as
  // disabled fields next to a white <input>.
  //
  // The same shade as a disabled one: to the person in front of it a field
  // that cannot be typed in is a field that cannot be typed in, and a form
  // that shows two greys for that is a form asking a question about the
  // difference.
  "[&:is(input,textarea):read-only]:bg-surface-disabled",
].join(" ");

/**
 * 32 / 40 / 44. A field is taller than a button now — the button scale tops out
 * at 32 for its default — so a row that has to line up asks for `fieldSize="sm"`
 * and gets 32 on both sides. `lg` is the other match, at 44.
 */
export const controlHeight = {
  sm: "h-8 px-2.5 text-subhead",
  md: "h-10 px-3 text-callout",
  lg: "h-11 px-3.5 text-body",
} as const;

type TextareaProps = ComponentProps<"textarea"> & {
  /** Off for a box that must keep one fixed height. */
  autoSize?: boolean;
};

export function Label({ className, ...props }: ComponentProps<"label">) {
  return <label className={cn("text-fg text-subhead font-medium", className)} {...props} />;
}

/**
 * The × that empties a control, written once so every control offers the same
 * way out of a value: the same target, the same hover, the same word for a
 * screen reader.
 *
 * Two shapes, because of where it has to sit. Inside a control's own trigger —
 * the selects, whose whole face is a `<button>` — it cannot be a button
 * itself: a button inside a button is invalid, and the browser resolves it by
 * dropping one. There it is a `<span>` that behaves like one and stays out of
 * the tab order, reached from the control it belongs to. Standing on its own —
 * beside a search box, on a filter chip — it is a real button and takes focus
 * like one, which is `focusable`.
 *
 * Either way the click stops where it is: the thing underneath is usually a
 * control that would open on it, and clearing a value is not asking to choose
 * another.
 */
export function ClearButton({
  label,
  onClear,
  focusable = false,
  disabled = false,
  className,
  iconClassName,
}: {
  /** What it clears, for a screen reader: "Clear", "Clear search". */
  label: string;
  onClear: () => void;
  /** A real `<button>`, for use anywhere that is not inside another one. */
  focusable?: boolean;
  disabled?: boolean;
  className?: string | undefined;
  iconClassName?: string | undefined;
}) {
  const shape = cn(
    "text-fg-subtle hover:bg-surface-inset hover:text-fg grid size-5 shrink-0 place-items-center",
    "rounded-full transition-colors disabled:pointer-events-none disabled:opacity-50",
    className,
  );
  const icon = <X className={cn("size-3.5", iconClassName)} aria-hidden />;
  const clear = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    onClear();
  };
  if (focusable) {
    return (
      <button
        type="button"
        aria-label={label}
        disabled={disabled}
        onClick={clear}
        className={shape}
      >
        {icon}
      </button>
    );
  }
  return (
    <span
      role="button"
      tabIndex={-1}
      aria-label={label}
      onClick={clear}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        clear(event);
      }}
      className={shape}
    >
      {icon}
    </span>
  );
}

export function Input({
  className,
  fieldSize = "md",
  ...props
}: ComponentProps<"input"> & { fieldSize?: keyof typeof controlHeight }) {
  return <input className={cn(controlClass, controlHeight[fieldSize], className)} {...props} />;
}

/**
 * A textarea that grows with what is typed in it, between a floor and a
 * ceiling.
 *
 * A fixed box is wrong twice: a one-line note wastes four lines of the form,
 * and a long one hides most of itself behind a scrollbar. It grows to fit and
 * stops at `max-h`, after which it scrolls — a field that can swallow the page
 * is its own problem.
 *
 * The height is released before it is measured, because `scrollHeight` of an
 * element already tall enough for its content is that height, not the content's
 * — without the reset the box could only ever grow.
 */
export function Textarea({ className, autoSize = true, ...props }: TextareaProps) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const fit = useCallback(() => {
    const element = ref.current;
    if (!element || !autoSize) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  }, [autoSize]);

  // On mount and whenever the value is set from outside — loading a record into
  // the form, say — not only as someone types.
  useEffect(fit, [fit, props.value]);

  return (
    <textarea
      ref={ref}
      onInput={fit}
      className={cn(
        controlClass,
        "text-callout min-h-24 px-3 py-2.5",
        // It sizes itself, so a drag handle would only fight the next keystroke.
        autoSize ? "max-h-56 resize-none overflow-y-auto" : "resize-y",
        className,
      )}
      {...props}
    />
  );
}

/**
 * An input with an icon on the left and optional controls on the right.
 *
 * The icons are decorative: the label is what names the field, so repeating it
 * to a screen reader would only add noise.
 */
export function InputGroup({
  icon: Icon,
  trailing,
  className,
  fieldSize = "md",
  ...props
}: ComponentProps<"input"> & {
  icon: LucideIcon;
  trailing?: ReactNode;
  fieldSize?: keyof typeof controlHeight;
}) {
  return (
    <div className="relative">
      <Icon
        aria-hidden
        className="text-fg-subtle pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
      />
      <Input
        fieldSize={fieldSize}
        className={cn("pl-9.5", trailing && "pr-11", className)}
        {...props}
      />
      {trailing ? (
        <div className="absolute top-1/2 right-1.5 -translate-y-1/2">{trailing}</div>
      ) : null}
    </div>
  );
}

/* --- What a form says when it refuses ---------------------------------------

   The browser's own answer is a yellow bubble over the first empty field that
   vanishes on the next keystroke, says nothing about the other five, and is
   drawn by the operating system in the operating system's language. `Form`
   turns that off and keeps the part worth keeping: the browser is still what
   decides whether a value is acceptable — `required`, `min`, `max`, `type`,
   the constraints already written on every field in this app — and the answer
   is written under the control, in red, in the app's own words, for every
   field at once and for as long as it is still true.                         */

type Validatable = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/** What is wrong under each field, by the id the field's label points at. */
export type FieldErrors = Record<string, string>;

const FieldErrorContext = createContext<FieldErrors>({});

/** Set on whatever this marked, so it never unsets a component's own state. */
const MARKED = "data-invalid-mark";
const DESCRIBED = "data-invalid-describes";

/**
 * The browser's verdict in this app's words.
 *
 * `validationMessage` is the browser's string: its language rather than the
 * app's — a Filipino form answering in English because Chrome is in English —
 * and written for whoever wrote the HTML ("Value must be greater than or equal
 * to 0"). The states are read instead and answered from the dictionary, with
 * the browser's own sentence kept as the last resort for the ones not named
 * here, `setCustomValidity` among them.
 */
function validityMessage(control: Validatable, t: Translate): string {
  const state = control.validity;
  const attr = (name: string) => control.getAttribute(name) ?? "";
  if (state.valueMissing) return t("form.required");
  if (state.typeMismatch)
    return t(attr("type") === "url" ? "form.invalidUrl" : "form.invalidEmail");
  if (state.badInput) return t("form.badNumber");
  if (state.rangeUnderflow) return t("form.min").replace("{min}", attr("min"));
  if (state.rangeOverflow) return t("form.max").replace("{max}", attr("max"));
  if (state.tooShort) return t("form.tooShort").replace("{count}", attr("minlength"));
  if (state.tooLong) return t("form.tooLong").replace("{count}", attr("maxlength"));
  if (state.patternMismatch || state.stepMismatch) return t("form.format");
  return control.validationMessage || t("form.invalid");
}

/**
 * The id the message belongs under.
 *
 * A control with an id of its own is its own field — `Field` points its label
 * and its error line at that same id. A picker drawn as a `<button>` has no
 * validity of its own and keeps a hidden input for it; that input names the
 * control it stands in for, so the message lands under the picker rather than
 * nowhere. See `data-validity-for` in `FkSelect`, `OptionSelect`, `DatePicker`.
 */
function errorKey(control: Validatable): string {
  return control.dataset.validityFor ?? control.id;
}

/** Red border, red focus ring, and the message read as part of the field. */
function mark(target: HTMLElement, key: string): void {
  /**
   * Never a field nobody was asked to fill in.
   *
   * A form fills some fields in itself and then locks them — the day a visit
   * is recorded, the club of whoever is recording it. Turning one of those red
   * is an accusation about the only thing on the form the person had no part
   * in. The browser bars a disabled control from validation and `check` skips
   * it for that reason; this is the same rule said again where the colour is
   * actually applied, so no path can reach it.
   */
  if ((target as Partial<HTMLInputElement>).disabled) return;
  if (target.getAttribute("aria-invalid") !== "true") {
    target.setAttribute("aria-invalid", "true");
    target.setAttribute(MARKED, "");
  }
  // Only where the field says nothing else already: a hint under a control is
  // a description too, and replacing it costs a screen reader that sentence.
  if (key && !target.hasAttribute("aria-describedby")) {
    target.setAttribute("aria-describedby", `${key}-error`);
    target.setAttribute(DESCRIBED, "");
  }
}

/**
 * Undo, but only what was done here.
 *
 * Some controls carry an `invalid` of their own — a phone number the API
 * refused, a picker told it is wrong by the screen around it — written by
 * React as the same attribute. Clearing it blindly would take that state down
 * with it and React, whose prop never changed, would never put it back.
 */
function unmark(target: HTMLElement): void {
  if (target.hasAttribute(MARKED)) {
    target.removeAttribute("aria-invalid");
    target.removeAttribute(MARKED);
  }
  if (target.hasAttribute(DESCRIBED)) {
    target.removeAttribute("aria-describedby");
    target.removeAttribute(DESCRIBED);
  }
}

/**
 * Every control in the form, read once: what to say, and where to send them.
 *
 * `speaks` is which fields are allowed to answer yet — one the person has
 * been in and left, or all of them once Save has been pressed. A field nobody
 * has touched is left alone however wrong it is: a form that goes red before
 * it has been filled in is a form scolding someone for not having started.
 *
 * `refused` is what the save waits on rather than `errors` being empty, and
 * `first` is only where to put the cursor: a control with no id has nowhere to
 * put a message, and a form that submitted anyway because the refusal had
 * nowhere to go would be the worst of both.
 */
function check(
  form: HTMLFormElement,
  t: Translate,
  speaks: (key: string) => boolean,
  rules?: (() => FieldErrors) | undefined,
): { errors: FieldErrors; first: HTMLElement | null; refused: boolean } {
  const errors: FieldErrors = {};
  let first: HTMLElement | null = null;
  let refused = false;
  for (const element of Array.from(form.elements)) {
    const control = element as Validatable;
    // Buttons, fieldsets, and anything disabled or read-only: nothing the
    // browser would have validated either.
    if (!("validity" in control) || !control.willValidate) continue;
    const key = errorKey(control);
    // The control the person sees, which for a hidden validity input is the
    // picker it belongs to.
    const target = (key ? form.ownerDocument.getElementById(key) : null) ?? control;
    if (control.validity.valid) {
      unmark(target);
      continue;
    }
    refused = true;
    first ??= target;
    if (!speaks(key)) {
      unmark(target);
      continue;
    }
    mark(target, key);
    // The first refusal per field wins: one line under a control, not three.
    if (key && !(key in errors)) errors[key] = validityMessage(control, t);
  }

  /*
   * And the form's own rules, for what no attribute can say: an address that
   * has to look like one, a date that has to fall before another, a picker
   * whose emptiness means "choose one from the list" rather than "fill this
   * in". They are keyed by the id the label already points at, so they mark,
   * clear and wait their turn exactly as a constraint does — a field the
   * person has not been in yet stays quiet whichever kind of refusal it is.
   */
  for (const [key, message] of Object.entries(rules?.() ?? {})) {
    if (!message || key in errors) continue;
    refused = true;
    const target = form.ownerDocument.getElementById(key);
    // A rule about a field that is not on screen — a section nobody has
    // opened — still refuses the save; there is simply nowhere to say so.
    if (!target) continue;
    first ??= target;
    if (!speaks(key)) {
      unmark(target);
      continue;
    }
    mark(target, key);
    errors[key] = message;
  }
  return { errors, first, refused };
}

function sameErrors(held: FieldErrors, found: FieldErrors): boolean {
  const keys = Object.keys(held);
  return (
    keys.length === Object.keys(found).length && keys.every((key) => held[key] === found[key])
  );
}

/**
 * A `<form>` that answers for itself.
 *
 * Drop-in for the element: same props, same `onSubmit` — which is called only
 * once every field is acceptable, so what is inside it stays the save and
 * nothing else. A refused submit puts the message under each field that needs
 * one, turns those controls red, and sends the cursor to the first of them.
 *
 * A field answers as soon as it has been left, not only when Save is pressed:
 * someone who empties a name and tabs on is told there and then, while they
 * are still beside it, rather than at the bottom of the form five fields
 * later. Until they have been in it a field says nothing — a form that goes
 * red before it has been filled in is scolding someone for not having started
 * — and once it has spoken it keeps up with the typing, mending its own line
 * the moment the value is acceptable.
 *
 * `validate` is for what the attributes cannot say — an address that has to
 * look like one, a date that has to fall before another. It answers with a
 * message per field id, is read the same way a constraint is, and is called
 * often enough that it must be a pure reading of the values and nothing else.
 */
export function Form({
  onSubmit,
  validate,
  children,
  ...props
}: ComponentProps<"form"> & { validate?: (() => FieldErrors) | undefined }) {
  const t = useTranslation();
  const form = useRef<HTMLFormElement>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  /** The fields that have had the cursor in them and lost it again. */
  const touched = useRef(new Set<string>());
  /** Save has been pressed: every field answers now, touched or not. */
  const asked = useRef(false);

  const speaks = (key: string) => asked.current || touched.current.has(key);

  const reread = () => {
    if (!form.current) return;
    const { errors: found } = check(form.current, t, speaks, validate);
    setErrors((held) => (sameErrors(held, found) ? held : found));
  };

  /**
   * Re-read after every render rather than on an event.
   *
   * `input` would cover the typing and miss the rest: a picker drawn as a
   * button sets its value through React, a date comes back from a calendar,
   * an absent-toggle empties the field beside it — none of them a keystroke,
   * all of them changes to whether this form can be saved. Every one of them
   * renders, so this runs, and `sameErrors` is what keeps that from being a
   * loop: an unchanged verdict sets no state.
   */
  // No dependency list on purpose: every render is the signal. The setState
  // inside is the chain the rule warns about, and `sameErrors` is what breaks
  // it — an unchanged verdict never reaches state, so the next render never
  // comes from here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!asked.current && touched.current.size === 0) return;
    reread();
  });

  return (
    <FieldErrorContext.Provider value={errors}>
      <form
        ref={form}
        // Ours, not the browser's.
        noValidate
        /**
         * Leaving a field is what lets it speak.
         *
         * React's `onBlur` is `focusout`, so it reaches the form from whatever
         * was left — an input, or the `<button>` a picker is drawn as, which
         * carries the same id its hidden validity input points at. Recorded by
         * id rather than by element, because the control that holds the value
         * and the control that holds its validity are not always the same one.
         */
        onBlur={(event) => {
          /**
           * Not on the way to Save, and nothing else.
           *
           * A press moves the focus before it completes — mousedown, blur,
           * then mouseup — so a line appearing under the field being left
           * pushes the button down between the press and the release, and the
           * click lands on nothing. Staying quiet costs nothing there: the
           * submit a moment later answers for every field at once.
           *
           * Only for a button that actually submits, which `.type` answers
           * for: a `<button>` with no type attribute of its own is a submit
           * button, and the pickers, drawn as `type="button"`, are not.
           *
           * Where the focus lands is no test at all, which is what this used
           * to ask. These forms sit in a modal `<dialog>`, and clicking the
           * sheet beside a field parks the focus on the dialog itself rather
           * than on the body — outside the form, and nothing to do with Save,
           * so the field being left said nothing at all. jsdom hands back no
           * related target there, so only a browser ever showed it.
           */
          const to = event.relatedTarget as HTMLButtonElement | null;
          if (to?.type === "submit") return;
          const left = event.target as HTMLElement;
          const key = left.dataset?.validityFor ?? left.id;
          if (!key) return;
          touched.current.add(key);
          reread();
        }}
        onSubmit={(event) => {
          asked.current = true;
          const {
            errors: found,
            first,
            refused,
          } = check(event.currentTarget, t, speaks, validate);
          setErrors(found);
          if (refused) {
            event.preventDefault();
            first?.focus();
            return;
          }
          onSubmit?.(event);
        }}
        {...props}
      >
        {children}
      </form>
    </FieldErrorContext.Provider>
  );
}

interface FieldProps {
  label: ReactNode;
  labelClassName?: string;
  htmlFor: string;
  error?: string | undefined;
  /** Shown only while there is no error — a field says one thing at a time. */
  hint?: ReactNode;
  /** Marks the label and lets the control keep its own `required`. */
  required?: boolean | undefined;
  className?: string;
  children: ReactNode;
}

/**
 * Label, control, and one line of feedback underneath.
 *
 * The error carries an icon as well as red, and is wired to the control by id
 * so a screen reader reads it as part of the field rather than as loose text.
 */
export function Field({
  label,
  labelClassName,
  htmlFor,
  error,
  hint,
  required,
  className,
  children,
}: FieldProps) {
  // The caller's own message comes first — a save the API refused names the
  // field it refused — and the form's check answers for everything else.
  const refused = useContext(FieldErrorContext)[htmlFor];
  const shown = error ?? refused;
  return (
    <div className={cn("grid gap-1.5", className)}>
      <Label htmlFor={htmlFor} className={labelClassName}>
        {label}
        {required ? (
          <span aria-hidden className="text-danger ml-0.5">
            *
          </span>
        ) : null}
      </Label>
      {children}
      {shown ? (
        <p
          id={`${htmlFor}-error`}
          role="alert"
          className="text-danger text-subhead flex items-start gap-1.5"
        >
          <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden />
          <span>{shown}</span>
        </p>
      ) : hint ? (
        <p id={`${htmlFor}-hint`} className="text-fg-subtle text-subhead">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A checkbox drawn as a rounded square with a real tick.
 *
 * The native input stays in the tree and keeps the keyboard and the screen
 * reader; only the box you see is ours.
 */
export function Checkbox({
  className,
  label,
  ...props
}: ComponentProps<"input"> & { label?: ReactNode }) {
  const box = (
    <span className="relative inline-grid shrink-0 place-items-center">
      {/* The input covers the drawn box rather than hiding beside it.

          `sr-only` clips it to a single pixel, which is fine when a <label>
          wraps the pair — the label carries the click. Bare, as in a table's
          selection column, there is no label to carry anything: the square is
          `aria-hidden` decoration, the input is a pixel somewhere under it, and
          the box cannot be ticked at all. Invisible and full-size, it is the
          click target itself, and every existing <label> still works because a
          label points at the input either way. */}
      <input
        type="checkbox"
        className={cn(
          "peer absolute inset-0 z-10 size-full cursor-pointer opacity-0",
          "disabled:cursor-not-allowed",
          className,
        )}
        {...props}
      />
      <span
        aria-hidden
        className={cn(
          "border-line-strong bg-surface-raised grid size-[18px] place-items-center rounded-[4px] border",
          "transition-[background-color,border-color] duration-[var(--duration-fast)]",
          "peer-checked:border-accent peer-checked:bg-accent peer-indeterminate:border-accent peer-indeterminate:bg-accent",
          // The marks live inside this box, so neither can be a `peer-` target
          // itself: a tick when it is on, a dash when it stands for a page of
          // rows only some of which are. Filled with no mark at all is what
          // "some" looked like before, which reads as "all".
          "peer-checked:[&>svg]:opacity-100",
          "peer-indeterminate:[&>span]:opacity-100",
          "peer-focus-visible:ring-[3px] peer-focus-visible:ring-[var(--accent-ring)]",
          "peer-disabled:opacity-40",
        )}
      >
        {/* Both marks share one grid cell, so they sit on top of each other
            rather than beside. */}
        <Check
          className="col-start-1 row-start-1 size-3 text-white opacity-0 transition-opacity"
          strokeWidth={3}
        />
        <span className="col-start-1 row-start-1 h-[2px] w-2.5 rounded-full bg-white opacity-0 transition-opacity" />
      </span>
    </span>
  );
  if (!label) return box;
  return (
    <label className="text-callout text-fg flex cursor-pointer items-center gap-2.5">
      {box}
      <span>{label}</span>
    </label>
  );
}

/**
 * A switch, for a setting that takes effect the moment it moves. Anything that
 * only takes effect on Save is a checkbox instead — the shape is the promise.
 */
/**
 * The two sizes a switch comes in.
 *
 * `md` is a setting on a page of settings, where it is the thing being looked
 * at. `sm` is a switch in a list of thirty — the columns picker — where the
 * row is the label and the switch is the state beside it; at the larger size
 * the rows read as thirty controls rather than one list.
 *
 * The knob slides the pill's width less its own and both insets, so the two
 * sizes are one rule at two scales rather than two sets of numbers.
 */
const TOGGLE_SIZE = {
  md: "h-[26px] w-[42px] after:top-[3px] after:left-[3px] after:size-5 peer-checked:after:translate-x-4",
  sm: "h-[18px] w-8 after:top-[3px] after:left-[3px] after:size-3 peer-checked:after:translate-x-[14px]",
} as const;

/**
 * What "on" looks like. Green is the app's answer for a setting that is simply
 * on or off; the accent is for a switch that stands for something being shown
 * — a column in a table — where green would read as approval of the column.
 */
const TOGGLE_TONE = {
  success: "peer-checked:bg-success",
  accent: "peer-checked:bg-accent",
} as const;

export function Toggle({
  className,
  label,
  description,
  size = "md",
  tone = "success",
  ...props
}: // `size` on an <input> is a number of characters, and a name taken twice
// intersects to `never` — so the element's own is dropped and this one means
// how big the switch is drawn.
Omit<ComponentProps<"input">, "size"> & {
  label?: ReactNode;
  description?: ReactNode;
  size?: keyof typeof TOGGLE_SIZE;
  tone?: keyof typeof TOGGLE_TONE;
}) {
  const control = (
    <span className="relative inline-flex shrink-0">
      {/*
        Invisible but over the switch rather than `sr-only` beside it: the pill
        below is `aria-hidden` and draws no clicks of its own, so an input
        tucked into a corner leaves the thing people actually press inert
        unless a <label> is wrapped around both. Sized to the pill, it is the
        press target, and the `peer-` classes still style the pill off it.
      */}
      <input
        type="checkbox"
        role="switch"
        className={cn(
          "peer absolute inset-0 z-10 size-full cursor-pointer opacity-0 disabled:cursor-default",
          className,
        )}
        {...props}
      />
      <span
        aria-hidden
        className={cn(
          "bg-surface-inset relative inline-block rounded-full",
          "transition-colors duration-[var(--duration-base)] ease-[var(--ease-out-soft)]",
          "after:absolute after:rounded-full after:bg-white",
          "after:shadow-[0_1px_3px_rgb(0_0_0/0.25)] after:transition-transform after:duration-[var(--duration-base)] after:ease-[var(--ease-out-soft)]",
          "peer-focus-visible:ring-[3px] peer-focus-visible:ring-[var(--accent-ring)]",
          "peer-disabled:opacity-40",
          TOGGLE_SIZE[size],
          TOGGLE_TONE[tone],
        )}
      />
    </span>
  );
  if (!label) return control;
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4">
      <span className="min-w-0">
        <span className="text-fg text-callout block font-medium">{label}</span>
        {description ? (
          <span className="text-fg-muted text-subhead mt-0.5 block">{description}</span>
        ) : null}
      </span>
      {control}
    </label>
  );
}

/** A radio, for one choice out of a short visible set. */
export function Radio({
  className,
  label,
  ...props
}: ComponentProps<"input"> & { label?: ReactNode }) {
  const dot = (
    <span className="relative inline-grid shrink-0 place-items-center">
      <input type="radio" className={cn("peer sr-only", className)} {...props} />
      <span
        aria-hidden
        className={cn(
          "border-line-strong bg-surface-raised size-[18px] rounded-full border",
          "transition-[border-color,box-shadow] duration-[var(--duration-fast)]",
          "peer-checked:border-accent peer-checked:border-[5.5px]",
          "peer-focus-visible:ring-[3px] peer-focus-visible:ring-[var(--accent-ring)]",
          "peer-disabled:opacity-40",
        )}
      />
    </span>
  );
  if (!label) return dot;
  return (
    <label className="text-callout text-fg flex cursor-pointer items-center gap-2.5">
      {dot}
      <span>{label}</span>
    </label>
  );
}
